import React, { useState, useEffect, useContext } from "react";
import "./TeaserEdit.scss";

import { Redirect } from "react-router";

import Button from "../../ui/Button/Button";
import Select from "../../ui/Select/Select";

import { FixedHeader, PrimaryHeader, SecondaryHeader } from "../../ui/Header";

import * as urls from "../../utils/urls";

import TeaserForm from "../../ui/TeaserForm/TeaserForm";
import CampaignOverview from "../../ui/CampaignOverview/CampaignOverview";

import useCampaignInfoEffect from "../../effects/useCampaignInfoEffect";

import ModerRejectReason from "../../ui/ModerRejectReason/ModerRejectReason";

import CampaignSelect from "../../components/CampaignsSelect";
import BigLabel from "../../ui/BigLabel/BigLabel";

import { TeaserDetailService, TeaserUpdateService } from "../../utils/api";

import { teaserStatus } from "../../constants";

import eventEmitter from "../../utils/EventEmitter";

import WsContext from "../../context/WsContext";
import NotificationContext from "../../context/NotificationContext";

import * as perm from "../../utils/perm";
import usePermissions from "../../hooks/usePermissions";
import useCategoryRejectReasons from "../../hooks/useCategoryRejectReasons";

import useTitle from "../../hooks/useTitle";
import { useTeaserFormSpellChecker } from "../../hooks/useTeaserFormSpellChecker";

const publishedOptions = [
  { label: "Активный", value: teaserStatus.active },
  { label: "Неактивный", value: teaserStatus.inactive },
  { label: "Отклонен", value: teaserStatus.reject },
  { label: "На модерации", value: teaserStatus.moderate },
  { label: "Удален", value: teaserStatus.delete }
];

function isValid(fields) {
  return (
    !!fields.cid &&
    !!fields.category &&
    !!fields.title &&
    !!fields.image &&
    !!fields.url &&
    fields.type !== undefined &&
    fields.type !== null &&
    fields.type !== "" &&
    (fields.published != teaserStatus.reject ||
      (fields.published == teaserStatus.reject &&
        fields.reject_reasons.length != 0))
  );
}

const TeaserEdit = ({ light, isModal, match, onModalClose }) => {
  const tid = match.params.id;

  const wsContext = useContext(WsContext);
  const notificationContext = useContext(NotificationContext);
  const permissions = usePermissions();

  const [fields, setFields] = useState({ reject_reasons: [] });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);

  const spellChecker = useTeaserFormSpellChecker();

  useTitle(`Редактирование анонса: ${fields.id} (CNTN.)`, [fields.id]);

  const {
    info: campaignInfo,
    loading: campaignInfoLoading
  } = useCampaignInfoEffect(fields.cid);

  let rejectReasons = useCategoryRejectReasons(fields.category);

  const handleSubmit = () => {
    const submit = () => {
      setLoading(true);

      TeaserUpdateService({ tid, data: fields })
        .then(() => {
          if (isModal) {
            eventEmitter.emit("teaser_update", { tid });
            onModalClose();
          } else {
            setSaved(true);
          }
        })
        .catch(e => {
          if (e.code == "TEASER_ALREADY_UPDATED") {
            notificationContext.showSimpleNotification({
              title: "Анонс был изменен",
              text: "Сохранение не совершено, анонс был изменен.",
              color: "red"
            });
          } else if (e.errors) {
            setErrors(e.errors);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    };

    spellChecker.onSubmit({ submit, fields });
  };

  const onChange = payload => {
    setFields({ ...fields, ...payload });
  };

  const onCampaignChange = selected => {
    setFields({ ...fields, cid: selected.value });
  };

  const onPublishedChange = selected => {
    setFields({ ...fields, published: selected.value });
  };

  const onReasonChange = ({ selectedItems: selected }) => {
    setFields({ ...fields, reject_reasons: selected });
  };

  const onRefreshClick = () => {
    TeaserDetailService({ tid }).then(data => {
      setFields(data);
      setShowRefresh(false);
    });
  };

  useEffect(() => {
    TeaserDetailService({ tid }).then(data => {
      setFields(data);
      setDefaultOptions([{ label: data.campaign_name, value: data.cid }]);
    });
  }, []);

  useEffect(() => {
    wsContext.eventEmitter.subscribe("teasers_update", data => {
      if (data.ids.includes(parseInt(tid))) {
        setShowRefresh(true);
      }
    });
  }, []);

  if (saved) {
    const redirectTo = {
      pathname: urls.teaserList(),
      search: `teasers_campaign=${fields.cid}`
    };
    return <Redirect to={redirectTo} />;
  }

  return (
    <div className="teaser-edit">
      {showRefresh && (
        <div className="teaser-edit__refresh">
          <BigLabel onClick={onRefreshClick}>
            Данные обновились. Обновить содержимое?
          </BigLabel>
        </div>
      )}
      <FixedHeader className="teaser-edit__header">
        <PrimaryHeader>
          <span className="header__title">Редактирование анонса</span>

          <Select
            disabled={!perm.canSetAnyStatus(permissions)}
            theme={light ? "" : "dark"}
            className="teaser-edit__status"
            value={fields.published}
            options={publishedOptions}
            onSelect={onPublishedChange}
          />

          <Button
            className="teaser-edit__create-btn"
            color="green"
            disabled={!isValid(fields) || showRefresh}
            onClick={handleSubmit}
          >
            Сохранить
          </Button>
        </PrimaryHeader>

        <SecondaryHeader>
          <div className="teaser-edit__controls">
            {perm.canChangeCid(permissions) ? (
              <CampaignSelect
                className="teaser-edit__campaign"
                placeholder="Выбор кампании"
                onSelect={onCampaignChange}
                value={fields.cid}
              />
            ) : (
              <Select
                className="teaser-edit__campaign"
                options={defaultOptions}
                value={fields.cid}
                disabled
              />
            )}

            {fields.published == teaserStatus.reject && (
              <ModerRejectReason
                selectedReasons={fields.reject_reasons}
                reasons={rejectReasons}
                onChange={onReasonChange}
              />
            )}
            <CampaignOverview
              info={campaignInfo}
              loading={campaignInfoLoading}
            />
          </div>
        </SecondaryHeader>
      </FixedHeader>
      <div className="content-body content-body__with-fixed-secondary">
        <TeaserForm
          fields={fields}
          onChange={onChange}
          errors={errors}
          campaignInfo={campaignInfo}
          spellChecker={spellChecker}
        />
      </div>
    </div>
  );
};

export default TeaserEdit;
